import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import Paper from '@/components/Paper'
import SubHeaders from '@/components/Headers/SubHeaders'
import {
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import type { FilterItemConfig } from '@/components/ui/data-table-customized'
import { useForm } from 'react-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import { generatePathDatacentral, paths } from '@/utils/path'

import {
    deleteDataAccessAssignment,
    toggleDataAccessAssignment,
} from '../../services'
import { useDataAccessAssignments } from '../../hooks/useDataAccessAssignments'
import { useResourceCatalog } from '../../hooks/useResourceCatalog'
import { computeImpactCount } from '../../helpers/impact-count'
import type { DataAccessAssignment, DataAccessListFilters } from '../../types'
import TestAsUserModal from '../TestAsUserModal'
import { buildDataAccessActions, buildDataAccessColumns } from './columns'

type StatusValue = NonNullable<DataAccessListFilters['status']>

const STATUS_OPTIONS: { value: StatusValue; labelKey: string }[] = [
    { value: 'all', labelKey: 'data-access-status-all' },
    { value: 'active', labelKey: 'data-access-status-active' },
    { value: 'inactive', labelKey: 'data-access-status-inactive' },
]

const DataAccessList = () => {
    const { t, i18n } = useTranslation('settings')
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()
    const [searchParams, setSearchParams] = useSearchParams()
    const [testTarget, setTestTarget] = useState<number | null>(null)

    const status = (searchParams.get('status') ?? 'all') as StatusValue
    const search = searchParams.get('search') ?? ''

    const { control } = useForm<{ status: StatusValue }>({
        defaultValues: { status },
    })

    const filters: DataAccessListFilters = useMemo(
        () => ({ status, search: search || undefined }),
        [status, search]
    )

    const { assignments, isFetching, renderState } =
        useDataAccessAssignments(filters)
    const { groups } = useResourceCatalog()

    const updateParam = useCallback(
        (key: string, value: string | undefined) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                if (value) next.set(key, value)
                else next.delete(key)
                next.delete('page')
                return next
            })
        },
        [setSearchParams]
    )

    const { mutateAsync: toggleMutate } = useMutationWithHandler(
        {
            mutationFn: ({ id, active }: { id: string; active: boolean }) =>
                toggleDataAccessAssignment(id, active),
        },
        {
            reactQueryKeys: QUERY_KEYS.DATA_ACCESS_ASSIGNMENTS,
            successMessage: t('data-access-toggle-success'),
        }
    )

    const { mutateAsync: deleteMutate } = useMutationWithHandler(
        { mutationFn: (id: string) => deleteDataAccessAssignment(id) },
        {
            reactQueryKeys: QUERY_KEYS.DATA_ACCESS_ASSIGNMENTS,
            successMessage: t('data-access-delete-success'),
        }
    )

    const renderImpactNote = (row: DataAccessAssignment) => {
        const count = computeImpactCount(row)
        const helper =
            (row.departments?.length ?? 0) > 0
                ? t('data-access-confirm-impact-departments')
                : null
        return (
            <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
                <span>{t('data-access-confirm-impact-count', { count })}</span>
                {helper && (
                    <p className="text-xs text-muted-foreground mt-1">
                        {helper}
                    </p>
                )}
            </div>
        )
    }

    const handleEdit = useCallback(
        (row: DataAccessAssignment) => {
            navigate(
                generatePathDatacentral(paths.dataAccessEdit, {
                    id: String(row.id),
                })
            )
        },
        [navigate]
    )

    const handleTest = useCallback((row: DataAccessAssignment) => {
        setTestTarget(row.id)
    }, [])

    const handleToggle = useCallback(
        (row: DataAccessAssignment) => {
            setConfirmation({
                open: true,
                title: row.is_active
                    ? t('data-access-toggle-off-title')
                    : t('data-access-toggle-on-title'),
                description: row.is_active
                    ? t('data-access-toggle-off-body')
                    : t('data-access-toggle-on-body'),
                customContent: renderImpactNote(row),
                onConfirm: () =>
                    toggleMutate({
                        id: String(row.id),
                        active: !row.is_active,
                    }),
            })
        },
        [setConfirmation, t, toggleMutate]
    )

    const handleDelete = useCallback(
        (row: DataAccessAssignment) => {
            setConfirmation({
                open: true,
                title: t('data-access-delete-title'),
                description: t('data-access-delete-body'),
                customContent: renderImpactNote(row),
                onConfirm: () => deleteMutate(String(row.id)),
            })
        },
        [setConfirmation, t, deleteMutate]
    )

    const columns = useMemo(
        () =>
            buildDataAccessColumns({
                t,
                lang: i18n.language,
                resourceGroups: groups,
            }),
        [t, i18n.language, groups]
    )

    const actions = useMemo(
        () =>
            buildDataAccessActions({
                t,
                onEdit: handleEdit,
                onTest: handleTest,
                onToggle: handleToggle,
                onDelete: handleDelete,
            }),
        [t, handleEdit, handleTest, handleToggle, handleDelete]
    )

    const filterItems = useMemo<FilterItemConfig[]>(
        () => [
            {
                name: 'status',
                type: 'select',
                label: t('status'),
                options: STATUS_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: t(opt.labelKey),
                })),
                onChange: (v: StatusValue) =>
                    updateParam('status', v === 'all' ? undefined : v),
            },
        ],
        [t, updateParam]
    )

    const stateComponent = renderState?.()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    { path: paths.settings, title: t('settings') },
                    { path: paths.dataAccess, title: t('data-access') },
                ]}
                actions={[
                    {
                        icon: <AddIcons />,
                        label: t('data-access-create-new'),
                        variant: 'contained',
                        onClick: () => navigate(paths.dataAccessCreate),
                    },
                ]}
            />

            <Paper className="space-y-2">
                <FilterBar
                    control={control}
                    items={filterItems}
                    storageKey="data-access-list"
                    hideSearch
                />
                <DataTableCustomized<DataAccessAssignment>
                    columns={columns}
                    rows={assignments}
                    rowKey={(row) => row.id}
                    size="small"
                    isFetching={isFetching}
                    loading={!assignments.length && isFetching}
                    emptyMessage={t('data-access-empty-all')}
                    actions={actions}
                    toolbar={{
                        search: {
                            value: search,
                            onChange: (v: string) =>
                                updateParam('search', v || undefined),
                            placeholder: t('data-access-search-placeholder'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'data-access-list',
                        },
                    }}
                />
            </Paper>

            {testTarget !== null && (
                <TestAsUserModal
                    open
                    onClose={() => setTestTarget(null)}
                    ruleId={testTarget}
                />
            )}
        </div>
    )
}

export default DataAccessList
